import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpResponse,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, from, map, switchMap, throwError } from "rxjs";

import { SKIP_ERROR_HANDLER } from "@core/interceptors/http-error.interceptor";
import { ApiUrlBuilderService } from "@core/services/common/api-url-builder.service";

/** Formato del archivo que genera el reporte. */
export type FormatoSalida = "PDF" | "EXCEL";

/** Segmento del endpoint y extensión del archivo, por formato de salida. */
const FORMATO_ARCHIVO: Record<FormatoSalida, { segmento: string; ext: string }> =
  {
    PDF: { segmento: "pdf", ext: "pdf" },
    EXCEL: { segmento: "xlsx", ext: "xlsx" },
  };

/** Archivo generado por Net, listo para descargar. */
export interface ArchivoReporte {
  blob: Blob;
  filename: string;
}

/**
 * Error ya leído del `Blob` de la respuesta. `sinDatos` distingue el 404 de alcance vacío
 * —que no es una falla— del resto de los errores.
 */
export interface ErrorReporte {
  status: number;
  errortxt: string | null;
  sinDatos: boolean;
}

/**
 * Reportes que Net arma y devuelve ya listos (`POST reports/org/{org}/{recurso}/{pdf|xlsx}`):
 * el front manda los filtros planos y solo descarga lo que vuelve.
 */
@Injectable({ providedIn: "root" })
export class ReporteArchivoService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiUrlBuilderService);

  /**
   * Va con `SKIP_ERROR_HANDLER` porque el error también llega como `Blob` y el interceptor
   * global no encuentra ahí el `errortxt`; lo resuelve `traducirError`.
   */
  public descargar(
    recurso: string,
    formato: FormatoSalida,
    filtros: object,
    nombreBase: string,
  ): Observable<ArchivoReporte> {
    const { segmento, ext } = FORMATO_ARCHIVO[formato];
    const org = (this.api.organization || "").trim();

    return this.http
      .post(
        this.api.buildBaseUrl("reports", "org", org, recurso, segmento),
        filtros,
        {
          responseType: "blob",
          observe: "response",
          context: new HttpContext().set(SKIP_ERROR_HANDLER, true),
        },
      )
      .pipe(
        map((res: HttpResponse<Blob>) => ({
          blob: res.body as Blob,
          filename: this.resolverNombre(res, ext, nombreBase),
        })),
        switchMap((archivo: ArchivoReporte) =>
          archivo.blob ? [archivo] : throwError(() => this.errorVacio()),
        ),
      );
  }

  /**
   * El cuerpo del error viaja como `Blob` por el `responseType`, así que hay que leerlo
   * para llegar al `errortxt` y al flag `sinDatos` del 404.
   */
  public traducirError(err: unknown): Observable<never> {
    const respuesta = err as HttpErrorResponse;
    const cuerpo: unknown = respuesta?.error;
    const status: number = respuesta?.status ?? 0;

    if (!(cuerpo instanceof Blob)) {
      return throwError(() => this.aErrorReporte(status, cuerpo));
    }

    return from(cuerpo.text()).pipe(
      switchMap((texto: string) =>
        throwError(() => this.aErrorReporte(status, this.parsear(texto))),
      ),
    );
  }

  /** El archivo ya viene armado por Net: solo hay que ofrecerlo al navegador. */
  public guardar({ blob, filename }: ArchivoReporte): void {
    const url: string = URL.createObjectURL(blob);
    const ancla: HTMLAnchorElement = document.createElement("a");
    ancla.href = url;
    ancla.download = filename;
    ancla.click();
    URL.revokeObjectURL(url);
  }

  /**
   * El nombre lo arma Net con la hora de Argentina y lo manda en `Content-Disposition`.
   * El header puede no estar expuesto por CORS, así que hay un nombre de respaldo con el
   * mismo formato, armado con la hora local.
   */
  private resolverNombre(
    res: HttpResponse<Blob>,
    ext: string,
    nombreBase: string,
  ): string {
    const header: string | null =
      res.headers?.get("Content-Disposition") ?? null;
    const match = header
      ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
      : null;

    if (match?.[1]) return decodeURIComponent(match[1].trim());
    return `${nombreBase}_${this.selloDeTiempo()}.${ext}`;
  }

  private selloDeTiempo(): string {
    const ahora = new Date();
    const dosDigitos = (valor: number): string => String(valor).padStart(2, "0");
    return (
      `${ahora.getFullYear()}${dosDigitos(ahora.getMonth() + 1)}${dosDigitos(ahora.getDate())}` +
      `_${dosDigitos(ahora.getHours())}${dosDigitos(ahora.getMinutes())}`
    );
  }

  private parsear(texto: string): unknown {
    try {
      return JSON.parse(texto);
    } catch {
      return null;
    }
  }

  private aErrorReporte(status: number, cuerpo: unknown): ErrorReporte {
    const datos = (cuerpo ?? {}) as { errortxt?: string; sinDatos?: boolean };
    return {
      status,
      errortxt: datos.errortxt ?? null,
      sinDatos: datos.sinDatos === true,
    };
  }

  private errorVacio(): ErrorReporte {
    return { status: 0, errortxt: null, sinDatos: false };
  }
}
